import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { TextColors, TextFieldBorderColors, TextFieldHoverColor } from '../../styles/Colors';
import { DefaultFocusIndicator } from '../../styles/FocusIndicators';
import { TabStyles } from '../../styles/TabStyles';
import { isCtrl, isEdge } from '../../utils/BrowserDetection';
import { makeTestId } from '../../utils/Declarative';
import { closest, findBlurRelatedTarget, generateId, scrollIntoView } from '../../utils/DomUtil';
import { ElemAttrs } from '../../utils/HtmlAttrs';
import { EventUtilsOptOut, FCProps, mergeEventHandlers } from '../../utils/ReactHelpers';
import { Chip, ChipProps } from '../Chips/Chip';
import { Combobox, ComboboxProps, ListItem } from './Combobox';

const { RowHeight } = TabStyles.Sizing;
const { Default: FontSize } = TabStyles.Typography.Sizing;

const BorderWidth = 1;
const PickerPadding = 3;
const ChipMargin = 4;

const LabelStyleName = TypeStyle.style({
  color: TextColors.Secondary,
  display: 'inline-block',
  fontSize: FontSize,
  marginBottom: 4,
  $nest: {
    '&:empty': { display: 'none' },
    '&.disabled': { color: TextColors.Disabled },
  }
});
const ChipPickerStyleName = TypeStyle.style({
  alignContent: 'flex-start',
  alignItems: 'flex-start',
  border: `${BorderWidth}px solid #cbcbcb`,
  borderRadius: 1,
  boxSizing: 'border-box',
  display: 'flex',
  flexWrap: 'wrap',
  fontSize: FontSize,
  lineHeight: `${RowHeight - 2}px`, // -2 for the 1px chip borders
  overflowY: 'auto',
  padding: PickerPadding,
  paddingRight: 18,
  position: 'relative',
  '-webkit-overflow-scrolling': 'touch',
  $nest: {
    '&:not([aria-disabled="true"])': {
      cursor: 'text',
      $nest: {
        '&:hover': { backgroundColor: TextFieldHoverColor, borderColor: TextFieldBorderColors.Hover },
        '&&[aria-invalid="true"]': { borderColor: '#c41723' },
      }
    },
    '&&.focus-within': {
      ...DefaultFocusIndicator, backgroundColor: 'transparent',
      $nest: { '&[aria-invalid="true"]': { boxShadow: `0 0 4px 0 #E62835` } }
    },
    '& > *': { margin: ChipMargin },
    '& > input': {
      fontSize: 'inherit',
      lineHeight: `${FontSize}px`,
      marginLeft: 0,
      marginRight: -1,
      outline: 'none',
    },
    '& > input:focus': isEdge ? { marginLeft: -1, marginRight: -3 } : { marginRight: -2 },
    '& > input:focus:first-child': { marginLeft: 4 },
    '& > .drag-indicator': {
      background: '#EB8F50',
      height: RowHeight + (2 * ChipMargin),
      margin: `0 -1px`,
      pointerEvents: 'none',
      width: 2,
    }
  }
});

export { ListItem };

// tslint:disable-next-line:max-line-length
export type ChipPickerProps = Pick<ComboboxProps, 'disabled' | 'listItems' | 'listMaxHeight' | 'onFilterItems' | 'placeholder' | 'stackingRootId' | 'zIndex'> & {
  /** The list of chips (i.e. currently selected values) to display. */
  children: Array<React.ReactElement<ChipProps>>;

  /** Label text to display above the picker. */
  label: string;

  /** Maximum number of lines of chips to grow to display. */
  maxLines?: number;

  /**
   * Invoked when the user adds a new value. Any validation, deduplication, etc. should occur here before passing
   * an updated list of chips into `children`.
   */
  onValueAdd?: (value: string, index: number) => void;

  /**
   * Invoked when the user reorders or removes value(s).
   * @param selectedValues the full list of selected values reflected after the change. Only values which were previously
   * present in `children` will be included -- i.e. it is not necessary to re-validate this list of values.
   */
  onValuesChange?: (selectedValues: string[]) => void;

  testId: string;
};

export type ChipPickerState = {
  /** `value` of the chip which was most recently focused. */
  activeChipValue: string;
  /** `0` if the "cursor" appears before the active chip, `1` if it appears after. */
  cursorOffset: 0 | 1;
  /** `-1` if no dragging is occurring, otherwise the index at which a pending drop event will splice */
  dragIndex: number;
  focusWithin: boolean;
  /** Tracks the *internal* multiselect state for manipulating chips -- orthogonal from `children`/`listItems` props. */
  selectedChips: { [key: string]: boolean };
};

/* Chip multi-selection disabled until after SP1
type ListContentCache = { [key: string]: React.ReactNode };

export function buildContentCache(listItems: ChipPickerProps['listItems']): ListContentCache {
  return listItems.reduce((cache: ListContentCache, value) => (cache[value.value] = value.content || value.value, cache), {});
}

export function buildSelectionCache(selectedValues: string[]): ChipPickerState['selectedChips'] {
  return selectedValues.reduce((cache: ChipPickerState['selectedChips'], value) => (cache[value] = true, cache), {});
}

export function reduceSelectionCache(cache: ChipPickerState['selectedChips']): string[] {
  return Object.keys(cache).filter(key => cache[key] === true);
}
*/

// function cloneAndInsert<T>(values: Readonly<T[]>, value: T, index: number): T[] {
//   const newValues = [...values];
//   newValues.splice(index, 0, value);
//   return newValues;
// }

const getPixelHeight = (lines: number): number =>
  ((BorderWidth + PickerPadding) * 2 + (ChipMargin * 2 + RowHeight) * Math.max(1, lines));

export class ChipPicker extends React.PureComponent<ChipPickerProps & Omit<ElemAttrs, keyof ChipPickerProps>, ChipPickerState> {
  private domRef: React.RefObject<HTMLDivElement> = React.createRef();
  private inputElement: HTMLInputElement | null = null;
  private readonly inputId: string = generateId();
  private readonly labelId: string = generateId();

  private findIndexOfChipValue = (value: string) => this.props.children.findIndex(chip => chip.props.value === value);
  private getChipElemAtIndex = (chipIndex: number): HTMLElement | null | undefined =>
    this.domRef.current?.querySelector<HTMLElement>(`[role="option"][aria-posinset="${chipIndex + 1}"]`)
  private getChipValue = (index: number): string => (this.props.children[index] || { props: { value: undefined } }).props.value;
  private getChipValues = () => this.props.children.map(child => child.props.value);
  private getLastChipValue = () => this.getChipValue(this.props.children.length - 1);

  public static defaultProps: Pick<ChipPickerProps, 'children' | 'maxLines'> = {
    children: [],
    maxLines: 3,
  };
  public state: ChipPickerState = {
    activeChipValue: this.getLastChipValue(),
    cursorOffset: 1,
    dragIndex: -1,
    focusWithin: false,
    selectedChips: {}
  };

  public componentDidUpdate(prevProps: ChipPickerProps, prevState: ChipPickerState): void {
    const { children, disabled } = this.props;
    const { children: prevChildren } = prevProps;

    if (!prevProps.disabled && disabled) {
      this.setState({ dragIndex: -1, focusWithin: false });
      return; // return early so that references to state below will be updated with these new values in the next call
    }

    if (children.length < prevChildren.length) {
      // when a chip is removed, put the cursor in its prior position
      const removedIndex = prevChildren.findIndex(chip => chip.props.value === prevState.activeChipValue);
      const activeChipValue = this.getChipValue(removedIndex) || this.getLastChipValue();
      this.setState(
        { activeChipValue, cursorOffset: removedIndex === children.length ? 1 : 0 },
        this.state.focusWithin ? this.focusInput : undefined
      );
    } else if (this.state.focusWithin && children.length === prevChildren.length + 1) {
      // when a single chip is added while picker is focused, assume chip was added by the user and put the cursor after it
      let activeChipIndex: number = 0;
      while (activeChipIndex < children.length - 1 && this.getChipValue(activeChipIndex) === prevChildren[activeChipIndex].props.value) {
        activeChipIndex++;
      }
      this.setState({ activeChipValue: this.getChipValue(activeChipIndex), cursorOffset: 1 });
    } else if (children.length > prevChildren.length) {
      // otherwise just put the cursor at the end, e.g. if the parent component just added a bunch of chips
      this.setState({ activeChipValue: this.getLastChipValue(), cursorOffset: 1 });
    }

    // make sure the most recent change is visible, unless actively dragging
    if (this.state.dragIndex < 0 && this.state.focusWithin) {
      if (children.length !== prevChildren.length) {
        scrollIntoView(this.inputElement);
        this.focusInput();
      } else {
        const chipIndex = this.findIndexOfChipValue(this.state.activeChipValue);
        scrollIntoView(this.getChipElemAtIndex(chipIndex));
      }
    }
  }

  /** Focuses the chip element at the specified chip index. */
  public focusChipAtIndex(chipIndex: number): void {
    const chipElem = this.getChipElemAtIndex(chipIndex);
    if (chipElem) { chipElem.focus(); }
  }

  /** Focuses the internal input element. */
  public focusInput(): void {
    if (this.inputElement) { this.inputElement.focus(); }
  }

  /** used for positioning the list popup */
  private getAnchorClientRect = () => this.domRef.current!.getBoundingClientRect();

  /* Chip multi-selection disabled until after SP1
  // private selectAllChips = (): void => this.setState({ selectedChips: buildSelectionCache(this.getChipValues()) });
  // private clearSelectedChips = () => this.setState({ selectedChips: {} });
  */
  private readonly chipKeyDownHandlers: { [key: string]: (currentIndex: number) => void } = Object.freeze({
    'ArrowLeft': () => this.setState({ cursorOffset: 0 }, this.focusInput),
    'ArrowRight': () => this.setState({ cursorOffset: 1 }, this.focusInput),
    'Home': () => this.setState({ activeChipValue: this.getChipValue(0), cursorOffset: 0 }, this.focusInput),
    'End': () => this.setState({ activeChipValue: this.getLastChipValue(), cursorOffset: 1 }, this.focusInput),
    'Backspace': currentIndex => this.removeValueAtIndex(currentIndex),
    'Delete': currentIndex => this.removeValueAtIndex(currentIndex),
    'Ctrl+a': () => { return; }, // this.selectAllChips,
    // 'Escape': this.clearSelectedChips,
  });

  private readonly inputKeyDownHandlers: { [key: string]: (focusables: HTMLElement[], currentIndex: number) => void } = Object.freeze({
    'ArrowLeft': (focusables, currentIndex) => {
      const nextIndex = Math.max(currentIndex - 1, 0);
      this.setState({ cursorOffset: currentIndex > 0 ? 1 : 0 }, () => focusables[nextIndex].focus());
    },
    'ArrowRight': (focusables, currentIndex) => {
      const nextIndex = Math.min(currentIndex + 1, focusables.length - 1);
      this.setState({ cursorOffset: currentIndex < focusables.length - 1 ? 0 : 1 }, () => focusables[nextIndex].focus());
    },
    'Home': () => this.setState({ activeChipValue: this.getChipValue(0), cursorOffset: 0 }, this.focusInput),
    'End': () => this.setState({ activeChipValue: this.getLastChipValue(), cursorOffset: 1 }, this.focusInput),
    'Backspace': (focusables, currentIndex) => this.inputKeyDownHandlers.ArrowLeft(focusables, currentIndex),
    'Delete': (focusables, currentIndex) => this.inputKeyDownHandlers.ArrowRight(focusables, currentIndex),
    // 'Ctrl+a': this.selectAllChips,
  });

  private handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = mergeEventHandlers(e => {
    const targetIsChip = (target: EventTarget): target is HTMLDivElement => (target as HTMLElement).getAttribute('role') === 'option';
    const targetIsInput = (target: EventTarget): target is HTMLInputElement => (target as HTMLElement).tagName === 'INPUT';
    const key = (isCtrl(e) ? 'Ctrl+' : '') + e.key;

    if (targetIsChip(e.target) && this.chipKeyDownHandlers[key]) {
      this.chipKeyDownHandlers[key](this.findIndexOfChipValue(this.state.activeChipValue));
      e.preventDefault();
      e.stopPropagation();
    } else if (targetIsInput(e.target) && e.target.value === '' && this.inputKeyDownHandlers[key]) {
      const focusables = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[role="option"], input'));
      const currentIndex = focusables.indexOf(e.target);
      this.inputKeyDownHandlers[key](focusables, currentIndex);
      e.preventDefault();
      e.stopPropagation();
    }
  }, e => this.props.onKeyDown?.(e));

  private findNearbyChipFromMouseEvent(e: React.MouseEvent<HTMLElement>, searchBackwards: boolean = true): HTMLElement | null {
    if (e.currentTarget.querySelector('[role="option"]') === null) { return null; }
    let node: Element = e.currentTarget;
    let { clientX, clientY } = e;
    while (node === e.currentTarget || node?.tagName === 'INPUT') {
      searchBackwards ? --clientX : ++clientX;
      node = document.elementFromPoint(clientX, clientY)!;
    }
    return closest(node, `[role="option"]`) as HTMLElement | null;
  }

  private getDragIndexFromDragEvent = (e: React.DragEvent<HTMLElement>): number => {
    const dragOnContainer = e.target === e.currentTarget;
    const inLeftGutter: boolean = dragOnContainer && e.nativeEvent.offsetX < (BorderWidth + PickerPadding + ChipMargin);
    const nearbyChip = dragOnContainer ?
      this.findNearbyChipFromMouseEvent(e, !inLeftGutter) :
      closest(e.target as HTMLElement, `[role="option"]`);
    return nearbyChip ? parseInt(nearbyChip.getAttribute('aria-posinset')!, 10) - (inLeftGutter ? 1 : 0) : this.getChipValues().length;
  }

  private handleDragOver: React.DragEventHandler<HTMLDivElement> = mergeEventHandlers(e => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const dragIndex = this.getDragIndexFromDragEvent(e);
    if (dragIndex >= 0 && dragIndex <= this.getChipValues().length) {
      this.setState({ dragIndex });
    }
  }, e => this.props.onDragOver?.(e));

  private handleDragLeave: React.DragEventHandler<HTMLDivElement> = mergeEventHandlers(e => {
    this.setState({ dragIndex: -1 });
  }, e => this.props.onDragExit?.(e));

  private handleDrop: React.DragEventHandler<HTMLDivElement> = mergeEventHandlers(e => {
    const to = this.state.dragIndex;
    const data = e.dataTransfer.getData('Text');
    this.setState({ dragIndex: -1 });
    if (!this.props.onValuesChange || !data) { return; }

    const from = this.findIndexOfChipValue(data);
    const values = [...this.getChipValues()];
    if (!isFinite(to) || from >= values.length || to > values.length) {
      return;
    }
    e.preventDefault();

    if (from < 0 && this.props.onValueAdd && this.props.listItems.findIndex(item => item.value === data)) {
      this.props.onValueAdd(data, to);
    } else {
      values.splice(to - (from >= to ? 0 : 1), 0, values.splice(from, 1)[0]);
      this.props.onValuesChange(values);
    }
  }, e => this.props.onDrop?.(e));

  /** pseudo-simulate a `<textarea>` by allowing mouse clicks to move the "cursor" around (i.e. between chips) */
  private handleMouseDown: React.MouseEventHandler<HTMLDivElement> = mergeEventHandlers(e => {
    if (e.currentTarget !== e.target) { return; }
    const inLeftGutter = e.nativeEvent.offsetX < (BorderWidth + PickerPadding + ChipMargin);
    const nearbyChip = this.findNearbyChipFromMouseEvent(e, !inLeftGutter);
    if (nearbyChip) {
      nearbyChip.focus();
      this.setState({ cursorOffset: inLeftGutter ? 0 : 1 }, this.focusInput);
    } else {
      this.setState({ activeChipValue: this.getLastChipValue(), cursorOffset: 1 });
    }
  }, e => this.props.onMouseDown?.(e));

  private handleBlur: React.FocusEventHandler<HTMLDivElement> = mergeEventHandlers(e => {
    if (!e.currentTarget.contains(findBlurRelatedTarget(e))) {
      this.setState({ focusWithin: false });
    }
  }, e => this.props.onBlur?.(e));

  private handleFocus: React.FocusEventHandler<HTMLDivElement> = mergeEventHandlers(e => {
    if (e.currentTarget === e.target) {
      this.focusInput();
    }
    if (!this.state.focusWithin) {
      this.setState({ focusWithin: true });
    }
  }, e => this.props.onFocus?.(e));

  private handleInputRef = (ref: HTMLInputElement) => this.inputElement = ref;

  private handleSelectValue = (value: string): void => {
    if (this.props.onValueAdd) {
      const insertionIndex = Math.max(0, this.findIndexOfChipValue(this.state.activeChipValue) + this.state.cursorOffset);
      this.props.onValueAdd(value, insertionIndex);
    }
  }

  private removeValueAtIndex = (index: number): void => {
    const { onValuesChange } = this.props;
    const newValues = this.getChipValues();
    newValues.splice(index, 1);
    if (onValuesChange) { onValuesChange(newValues); }
  }

  private renderChips(): Array<React.ReactElement> {
    const { children, disabled } = this.props;
    const chipCount = React.Children.count(children);
    return React.Children.map(children, (child: React.ReactElement<FCProps<typeof Chip>>, index: number) => {
      const value = child.props.value;
      return React.cloneElement(child, {
        'aria-atomic': true,
        'aria-disabled': disabled || child.props['aria-disabled'],
        'aria-posinset': index + 1,
        'aria-selected': this.state.selectedChips[value],
        'aria-setsize': chipCount,
        key: value,
        role: 'option',
        ...(!disabled && {
          /* Chip multi-selection disabled until after SP1
          // onClick: () => this.setState(prevState => ({
            selectedChips: { ...prevState.selectedChips, [value]: !prevState.selectedChips[value] }
          })),
          */
          draggable: true,
          onDragStart: (e: React.DragEvent) => {
            e.dataTransfer.setData('Text', value);
            e.dataTransfer.effectAllowed = 'move';
          },
          onFocus: () => this.setState({ activeChipValue: value }),
          onRemove: () => this.removeValueAtIndex(index),
          tabIndex: -1,
        }),
      });
    });
  }

  private renderCombobox(props: (ComboboxProps | ChipPickerProps)): React.ReactElement<ComboboxProps> {
    return <Combobox
      {...props}
      getAnchorClientRect={this.getAnchorClientRect}
      id={this.inputId}
      inputRef={this.handleInputRef}
      key='combobox'
      onSelectValue={this.handleSelectValue}
      testId={makeTestId(props.testId, 'ChipPicker')}
    />;
  }

  public render(): React.ReactNode {
    // tslint:disable:max-line-length deprecation
    const { children, className, disabled, onFilterItems, label, maxLines, listItems, listMaxHeight, onMouseDown, onValueAdd, onValuesChange, placeholder, stackingRootId, style, testId, zIndex, ...htmlProps } = this.props;
    const { activeChipValue, cursorOffset, dragIndex } = this.state;

    const chips = this.renderChips();
    if (!disabled) {
      if (dragIndex < 0) {
        // insert the combobox between chips so the whole control feels like a big text field with discrete chips
        const activeChipIndex = this.findIndexOfChipValue(activeChipValue);
        const placeholderText = chips.length > 0 ? undefined : placeholder;
        const combobox = this.renderCombobox({ 'aria-label': htmlProps['aria-label'], 'aria-labelledby': htmlProps['aria-labelledby'], disabled, listItems, listMaxHeight, onFilterItems, placeholder: placeholderText, stackingRootId, testId, zIndex });
        const comboIndex = activeChipIndex + cursorOffset;
        chips.splice(comboIndex, 0, combobox);
      } else {
        // insert the drag indicator at the current dragIndex
        const dragIndicator = <div className='drag-indicator' key='drag-indicator' />;
        chips.splice(dragIndex, 0, dragIndicator);
      }
    }
    // tslint:enable:max-line-length deprecation

    return <>
      <label // field label
        children={label}
        className={TypeStyle.classes(LabelStyleName, { disabled })}
        data-tb-test-id={makeTestId(testId, 'ChipPicker', 'Label')}
        htmlFor={this.inputId}
        id={this.labelId}
      />
      <div // picker
        {...EventUtilsOptOut}
        aria-disabled={disabled}
        aria-labelledby={this.labelId}
        aria-orientation='horizontal'
        data-tb-test-id={makeTestId(testId, 'ChipPicker')}
        spellCheck={false} // TFS 959869, overridable via resting below
        {...htmlProps}
        children={chips}
        className={TypeStyle.classes(ChipPickerStyleName, className, { 'focus-within': this.state.focusWithin && !disabled })}
        {...(!disabled && {
          onBlur: this.handleBlur,
          onDragLeave: this.handleDragLeave,
          onDragOver: this.handleDragOver,
          onDrop: this.handleDrop,
          onFocus: this.handleFocus,
          onKeyDown: this.handleKeyDown,
          onMouseDown: this.handleMouseDown,
          tabIndex: -1,
        })}
        ref={this.domRef}
        role='listbox'
        style={{ maxHeight: getPixelHeight(maxLines!), ...style }}
      />
    </>;
  }
}
